import { makeId } from '@gotti/core';
import type { MasterStateStore } from '../state/MasterStateStore.js';

export enum DYNAMIC_AREA_STATE {
  PENDING = 'pending',
  READY = 'ready',
  DRAINING = 'draining',
  CLOSED = 'closed',
}

export type DynamicAreaPolicy = {
  ephemeral: boolean;
  emptyTtlMs?: number;
  maxClients?: number;
};

export type DynamicAreaRecord = {
  areaId: string;
  type: string;
  gameId?: string;
  areaKey?: string;
  configData?: any;
  hostServerId?: string | number;
  generation: number;
  state: DYNAMIC_AREA_STATE;
  /**
   * The member ids — authoritative ONLY when the table is NOT spilling (memory
   * mode, i.e. no redis), which is exactly today's behaviour. When a spill
   * store is configured the ids live there instead and this array stays EMPTY
   * by design (the second governing rule: nothing that grows with the number
   * of clients is duplicated in process memory). Read members through
   * `DynamicAreaTable.getMembers()` / `MasterServer.getAreaMembers()` if you
   * need the list in both modes; read `memberCount` if you need the size.
   */
  members: string[];
  /**
   * Size of the membership, maintained on BOTH backings and the synchronous
   * authority for every "is it full / is it empty" check. Optional on the type
   * only because a record can arrive over the wire in a `HostReport` (or from
   * an older host) without one — every record the table owns has it set, and
   * `memberCountOf` is the safe reader.
   */
  memberCount?: number;
  policy: DynamicAreaPolicy;
  /**
   * Who created this dynamic area: the area id that called `createArea`, or
   * `'master'` for a pipeline-auto-created one (client `joinArea` /
   * `getInitialAreas`). Only the owner (or a master) may `destroyArea` it.
   */
  createdBy?: string | number;
};

export type CreateDynamicAreaInput = {
  type: string;
  gameId?: string;
  areaKey?: string;
  configData?: any;
  hostServerId?: string | number;
  policy?: Partial<DynamicAreaPolicy>;
  createdBy?: string | number;
};

/**
 * The size of an area's membership, from whichever half of the record is
 * authoritative. Table-owned records always carry `memberCount`; a raw
 * wire/host-report record may only carry the array.
 */
export function memberCountOf(record: DynamicAreaRecord): number {
  return record.memberCount ?? record.members.length;
}

export type DynamicAreaTableOptions = {
  /**
   * Where per-area members SPILL to. Resolved per call (the master's redis
   * handle is created inside `initialize()`, long after this table is
   * constructed), and `undefined` means "no spill" — memory mode, where the
   * member arrays stay on the records exactly as they always have.
   */
  spillStore?: () => MasterStateStore | undefined;
  /** Reported instead of thrown — a spill write must never break membership accounting. */
  onSpillError?: (err: unknown) => void;
};

export class DynamicAreaTable {
  private readonly records: Map<string, DynamicAreaRecord> = new Map();
  private readonly byKey: Map<string, string> = new Map();
  private generationCounter = 0;

  /**
   * One serialized chain per area, so spill writes and reads for the same area
   * are ordered and a `getMembers()` issued after a write observes it. Bounded
   * by the number of areas with an in-flight write (an entry deletes itself
   * when its chain drains), never by the number of clients.
   */
  private readonly spillChains: Map<string, Promise<unknown>> = new Map();

  constructor(private readonly opts: DynamicAreaTableOptions = {}) {}

  /** The spill target, or `undefined` in memory mode. */
  private get spill(): MasterStateStore | undefined {
    return this.opts.spillStore?.();
  }

  /** Set the first time a spill target is seen — see `migrateRetainedMembers`. */
  private spillStarted = false;

  /**
   * A store that appears AFTER records already exist would otherwise strand
   * every member array those records still carry: `getMembers` would read an
   * empty spill and the counts would drift, silently and permanently.
   *
   * Not reachable in the normal boot order (`initialize()` creates the redis
   * handle before any area can be created) — but nothing enforced that
   * ordering, so this makes the transition CORRECT instead of merely unlikely:
   * on the first spilling write, everything already held in memory is written
   * through and released. One pass, once, and a no-op for the overwhelmingly
   * common case where the table has been spilling from the start.
   */
  private migrateRetainedMembers(store: MasterStateStore) {
    this.records.forEach((rec) => {
      if (!rec.members.length) return;
      const ids = [...rec.members];
      rec.members = [];
      rec.memberCount = ids.length;
      this.enqueueSpill(store, rec.areaId, async (s) => {
        for (const id of ids) {
          await s.addAreaMember(rec.areaId, id);
        }
      });
    });
  }

  private chainSpill<T>(areaId: string, op: (store: MasterStateStore) => Promise<T>): Promise<T> | undefined {
    const store = this.spill;
    if (!store) return undefined;
    if (!this.spillStarted) {
      // Before this op is queued, so a migrated area's ids reach the store
      // ahead of whatever write triggered the migration.
      this.spillStarted = true;
      this.migrateRetainedMembers(store);
    }
    return this.enqueueSpill(store, areaId, op);
  }

  private enqueueSpill<T>(store: MasterStateStore, areaId: string, op: (store: MasterStateStore) => Promise<T>): Promise<T> {
    const run = () => op(store);
    const prev = this.spillChains.get(areaId);
    // `then(run, run)` rather than `then(run)`: a failed spill write must not
    // poison every later write for the same area.
    const next = prev ? prev.then(run, run) : run();
    const tracked = next.catch((err) => {
      this.opts.onSpillError?.(err);
      return undefined;
    });
    this.spillChains.set(areaId, tracked);
    tracked.then(() => {
      if (this.spillChains.get(areaId) === tracked) {
        this.spillChains.delete(areaId);
      }
    });
    return next;
  }

  /**
   * The net effect of spill writes that have been ISSUED but not yet applied
   * to the store, per area (+1 per queued join, -1 per queued leave).
   *
   * Without it, reconciling straight from the store's cardinality would be
   * wrong the moment two reports land in one tick: the count that comes back
   * with the FIRST write reflects the store before the second one ran, so
   * writing it onto the record would silently undo the second report's effect
   * — a join landing in the round trip of the leave that emptied an area would
   * be erased, and the area torn down under the player who just joined. One
   * number per area with an in-flight write, never per client.
   */
  private readonly queuedDelta: Map<string, number> = new Map();

  private queueDelta(areaId: string, delta: number) {
    this.queuedDelta.set(areaId, (this.queuedDelta.get(areaId) ?? 0) + delta);
  }

  /**
   * Retire one queued write's entry from the ledger and report what is still
   * outstanding. Split out from `settleDelta` deliberately: EVERY queued write
   * must drop its entry, including one whose store call REJECTED, while only a
   * successful one may reconcile or answer.
   *
   * A queued delta that is never dropped is not a stalled write — the chain
   * keeps running and `onSpillError` reports the failure — it is permanent,
   * SILENT liveness loss for that area: `queuedDelta` stays non-zero forever,
   * so every later settle sees `remaining !== 0`, returns `null`, and the area
   * can never again report EMPTY. A `whenEmpty` destroy would then only ever
   * complete via its `timeoutMs` force, an ephemeral area's empty-reap would
   * never arm, and `memberCount` would decay into an uncorrected optimistic
   * counter that capacity checks still read. One transient redis blip is
   * enough to cause it, which is exactly the failure this table's own
   * "a failed spill write must not poison every later write" rule exists to
   * prevent.
   *
   * `null` means MY LEDGER WAS RESET — do not answer. `adopt` and `markClosed`
   * both `delete` an area's entry SYNCHRONOUSLY while chained writes for it are
   * still outstanding, so a landing write can genuinely find no entry of its
   * own. Retiring "nothing" by writing back `0 - delta` RE-CREATES the entry
   * and reintroduces exactly the permanent silence described above:
   *
   *  - via `markClosed`, a stranded Map entry per destroyed-area-with-a-racing-
   *    write, addressed by nothing and deleted by nothing;
   *  - via `adopt` — the severe one — the id is being RE-adopted, so the write
   *    poisons the ledger of a NEW incarnation that never issued it (and
   *    `adopt`'s own reconcile guard then fails permanently too). That runs
   *    from `handleHostReport`, i.e. the restart/reconnect path, with
   *    membership writes plausibly in flight.
   *
   * A reset write has nothing to retire and nothing to say: its own count is
   * from a life the table has already discarded, and the rebuild `adopt`
   * queued behind it carries the truth. `settleDelta` treats `null` exactly as
   * it treats "more writes outstanding" — it stays silent and lets a later,
   * legitimate settle answer.
   *
   * RESIDUAL, stated so it is not mistaken for closed: this fix closes the
   * ABSENT-entry case above — it does not close the case where a reset is
   * followed by a NEW queued write for the SAME id before the stale write
   * lands. `dropDelta` cannot tell the two writes apart (there is nothing on
   * the ledger entry to distinguish "my own delta" from "a later incarnation's
   * delta"), so the stale write finds the new entry and retires it as if it
   * were its own. This is PRE-EXISTING — reproduced with byte-identical
   * outcomes against this file before this fix, so it is not a regression the
   * fix introduced, and the fix is a strict improvement over what shipped
   * before it. Two reproduced consequences, both still open:
   *
   *  - a stale write with a negative (leave) offset can retire a new write
   *    early: every member leaves, the store settles to `[]`, but the LAST
   *    leave's own delta was already retired by the stale one, so it answers
   *    `{empty:false}` and `memberCount` sticks at 1 with no members left to
   *    correct it. Nobody is left to join or leave, so `whenEmpty` completes
   *    only via its `timeoutMs` force, the ephemeral empty-reap never arms,
   *    and the area leaks;
   *  - a stale write with a positive (join) offset can make a real leave
   *    settle as "last" while a join is still in flight: `settleDelta`
   *    answers `{empty:true}` and the caller tears the area down, while the
   *    store then contains a live member — an area destroyed under a live
   *    player. This is verbatim the hazard `queuedDelta` exists to prevent,
   *    reopened by a stale write it cannot recognise as stale.
   *
   * Closing it needs a per-area EPOCH: stamp every queued write with the
   * generation it was issued under (bumped by `adopt`/`markClosed` on reset),
   * so `dropDelta` can compare the write's epoch against the ledger's current
   * one and discard — not retire — anything from a prior epoch instead of
   * retiring whatever entry happens to be there. Not implemented here: see
   * `docs/superpowers/plans/2026-08-06-dynamic-area-ledger-epoch.md`.
   */
  private dropDelta(areaId: string, delta: number): number | null {
    const queued = this.queuedDelta.get(areaId);
    if (queued === undefined) return null;
    const remaining = queued - delta;
    if (remaining === 0) {
      this.queuedDelta.delete(areaId);
    } else {
      this.queuedDelta.set(areaId, remaining);
    }
    return remaining;
  }

  /**
   * A queued op just applied: drop its own delta, and answer ONLY if this was
   * the LAST write in flight for the area (`remaining === 0`). Then — and only
   * then — the store's cardinality reflects every report received so far, so it
   * is reconciled onto the record and returned. Otherwise `null`: not this
   * write's call to make, and a later settle will answer.
   *
   * The "answer only when last" rule is the whole correctness argument, and the
   * obvious-looking alternative is WRONG: projecting the outstanding writes as
   * `storeCount + remaining` treats every in-flight op as a guaranteed ±1, which
   * is the blind counter this design rejects, merely relocated into the round
   * trip. A leave reported TWICE for the same client (a disconnect plus an
   * explicit leave — the shape that actually happens) then reads
   * `max(0, 1 - 1) = 0` while another player is still in the area: the first
   * write reports "empty", the record's count is clobbered to 0 (defeating the
   * caller's own re-check), and a live match is destroyed under the remaining
   * player — while memory mode, guarded by `indexOf`, correctly does nothing.
   * Deferring to the last settle asks the SET the question instead of guessing:
   * `SREM` of an absent member removes nothing, so the final `SCARD` is simply
   * the truth.
   *
   * It also makes "the area emptied" fire exactly once when several real leaves
   * land together: only one of them is last.
   *
   * `dropDelta`'s `null` (my ledger was reset — see there) is silent for the
   * same reason `remaining !== 0` is: this write is not the one entitled to
   * answer. `!== 0` covers both without a second branch.
   */
  private settleDelta(areaId: string, delta: number, storeCount: number): number | null {
    if (this.dropDelta(areaId, delta) !== 0) return null;
    this.reconcileCount(areaId, storeCount);
    return storeCount;
  }

  /** Push the store's authoritative post-op count back onto the record. */
  private reconcileCount(areaId: string, count: number) {
    const rec = this.records.get(areaId);
    if (rec) {
      rec.memberCount = count;
    }
  }

  public create(input: CreateDynamicAreaInput): DynamicAreaRecord {
    const generation = ++this.generationCounter;
    const areaId = `${input.type}:${makeId(12)}`;
    const record: DynamicAreaRecord = {
      areaId,
      type: input.type,
      gameId: input.gameId,
      areaKey: input.areaKey,
      configData: input.configData,
      hostServerId: input.hostServerId,
      generation,
      state: DYNAMIC_AREA_STATE.PENDING,
      members: [],
      memberCount: 0,
      policy: {
        ephemeral: input.policy?.ephemeral ?? true,
        emptyTtlMs: input.policy?.emptyTtlMs,
        maxClients: input.policy?.maxClients,
      },
      createdBy: input.createdBy,
    };
    this.records.set(areaId, record);
    if (input.areaKey) {
      this.byKey.set(input.areaKey, areaId);
    }
    return record;
  }

  public get(areaId: string): DynamicAreaRecord | undefined {
    return this.records.get(areaId);
  }

  /**
   * Insert an externally-reported live record verbatim (reconciliation: a host
   * re-reports its running instances to a restarted master). Preserves the
   * record's id/generation/members and keeps future generations monotonic.
   *
   * THIS is how a restarted master re-learns its members — from the servers
   * that are actually alive, never from the store. In spill mode the reported
   * ids are written THROUGH to the store (replacing whatever was there, which
   * may be stale from a previous life) and the record's array is released, so
   * the spill is rebuilt from live truth rather than trusted as truth.
   */
  public adopt(record: DynamicAreaRecord): DynamicAreaRecord {
    if (this.records.has(record.areaId)) {
      throw new Error(`Cannot adopt area instance ${record.areaId}: already tracked.`);
    }
    const reported = record.members ?? [];
    record.members = reported;
    // The REPORT is the truth here, not any count that rode along with it: a
    // live host enumerating its members is exactly the source this whole
    // reconciliation path trusts.
    record.memberCount = reported.length;
    this.records.set(record.areaId, record);
    if (this.spill) {
      const ids = [...reported];
      record.members = [];
      // A fresh incarnation starts from the report, so any delta left over
      // from a previous life of this id is meaningless.
      this.queuedDelta.delete(record.areaId);
      this.chainSpill(record.areaId, async (store) => {
        await store.clearAreaMembers(record.areaId);
        for (const id of ids) {
          await store.addAreaMember(record.areaId, id);
        }
        // Same "answer only when last" rule as settleDelta: if reports have
        // already been queued behind this rebuild, their own settles carry the
        // truth and this one must not overwrite it with the report's snapshot.
        if ((this.queuedDelta.get(record.areaId) ?? 0) === 0) {
          this.reconcileCount(record.areaId, ids.length);
        }
      });
    }
    if (record.areaKey) {
      this.byKey.set(record.areaKey, record.areaId);
    }
    if (record.generation > this.generationCounter) {
      this.generationCounter = record.generation;
    }
    return record;
  }

  public getByKey(areaKey: string): DynamicAreaRecord | undefined {
    if (!areaKey) return undefined;
    const id = this.byKey.get(areaKey);
    return id ? this.records.get(id) : undefined;
  }

  public list(filter?: { type?: string; gameId?: string }): DynamicAreaRecord[] {
    let out = [...this.records.values()];
    if (filter?.type !== undefined) {
      out = out.filter((r) => r.type === filter.type);
    }
    if (filter?.gameId !== undefined) {
      out = out.filter((r) => r.gameId === filter.gameId);
    }
    return out;
  }

  public addMember(areaId: string, clientId: string): DynamicAreaRecord {
    const rec = this.requireRecord(areaId);
    if (this.spill) {
      // Optimistic, then corrected from the store's own cardinality when the
      // write lands: nothing local can tell whether this id was already a
      // member, and the count feeds capacity checks that must not stall on a
      // round trip.
      rec.memberCount = memberCountOf(rec) + 1;
      this.queueDelta(areaId, 1);
      this.chainSpill(areaId, async (store) => {
        let count: number;
        try {
          ({ count } = await store.addAreaMember(areaId, clientId));
        } catch (err) {
          // The write never landed, so it has nothing to reconcile FROM — but
          // its ledger entry must still go, or it silences every later settle
          // for this area forever. Rethrown so `onSpillError` still reports it.
          this.dropDelta(areaId, 1);
          throw err;
        }
        this.settleDelta(areaId, 1, count);
      });
      return rec;
    }
    if (!rec.members.includes(clientId)) {
      rec.members.push(clientId);
    }
    rec.memberCount = rec.members.length;
    return rec;
  }

  /**
   * Remove a member and report whether the area is now EMPTY — the signal
   * `MasterServer.notifyAreaMembership` branches on to complete a pending
   * `whenEmpty` destroy or arm an ephemeral empty-reap.
   *
   * In memory mode that answer is exact and synchronous, exactly as it has
   * always been. In SPILL mode there is no local member list to derive it
   * from, so the sync answer is deliberately `false` and the authoritative one
   * arrives on `settled` (the store's post-`SREM` cardinality). It is NOT
   * derived from a local `count--`: a leave reported twice, or a leave for a
   * client that was never a member, would drive that counter to zero and tear
   * down an area that still has players in it — while memory mode, guarded by
   * `indexOf`, would do nothing. A tear-down decision may lag by a round trip;
   * it may not be wrong.
   */
  public removeMember(areaId: string, clientId: string): { empty: boolean, settled?: Promise<{ empty: boolean }> } {
    const rec = this.requireRecord(areaId);
    if (this.spill) {
      rec.memberCount = Math.max(0, memberCountOf(rec) - 1);
      this.queueDelta(areaId, -1);
      const settled = this.chainSpill(areaId, async (store) => {
        let count: number;
        try {
          ({ count } = await store.removeAreaMember(areaId, clientId));
        } catch (err) {
          // Same rule as the add path, and the answer is doubly constrained
          // here: a write that never landed must NEVER report "empty" — the
          // member it failed to remove is still in the set. Dropping the entry
          // (rather than leaving it, or answering from it) is what keeps a
          // later HEALTHY leave able to report empty at all.
          this.dropDelta(areaId, -1);
          throw err;
        }
        // EMPTY only when this is the LAST write in flight for the area, and
        // then straight from the SET's own cardinality. `null` (more writes
        // still queued) is not "empty" and not "not empty" — it is "a later
        // settle answers", which is why it must not be coerced to a number
        // here. See settleDelta for the two races this gets right and the
        // projection that gets both wrong.
        return { empty: this.settleDelta(areaId, -1, count) === 0 };
      })!;
      // A failed spill write resolves NOT-empty: never tear an area down on
      // state we could not read.
      return { empty: false, settled: settled.catch(() => ({ empty: false })) };
    }
    const idx = rec.members.indexOf(clientId);
    if (idx > -1) {
      rec.members.splice(idx, 1);
    }
    rec.memberCount = rec.members.length;
    return { empty: rec.members.length === 0 };
  }

  /**
   * The member ids, in either mode — the record array in memory mode, the
   * spill in redis mode (ordered behind that area's in-flight writes, so a
   * read issued after a write observes it).
   */
  public getMembers(areaId: string): Promise<string[]> {
    if (this.spill) {
      return this.chainSpill(areaId, (store) => store.getAreaMembers(areaId))!
        .catch(() => [] as string[]);
    }
    return Promise.resolve([...(this.records.get(areaId)?.members ?? [])]);
  }

  public markReady(
    areaId: string,
    opts: { hostServerId: string | number; generation?: number },
  ): DynamicAreaRecord {
    const rec = this.transition(areaId, DYNAMIC_AREA_STATE.READY);
    rec.hostServerId = opts.hostServerId;
    if (opts.generation !== undefined) {
      rec.generation = opts.generation;
    }
    return rec;
  }

  public markDraining(areaId: string): DynamicAreaRecord {
    return this.transition(areaId, DYNAMIC_AREA_STATE.DRAINING);
  }

  public markClosed(areaId: string): DynamicAreaRecord {
    const rec = this.transition(areaId, DYNAMIC_AREA_STATE.CLOSED);
    this.records.delete(areaId);
    // The area is gone: drop its spill row so the store cannot accumulate dead
    // areas. Chained (not fired past the queue) so a membership write still in
    // flight cannot re-create the key behind the delete.
    this.queuedDelta.delete(areaId);
    this.chainSpill(areaId, (store) => store.clearAreaMembers(areaId));
    // Only clear the key mapping if it still points at THIS instance — the key
    // may have been re-pointed to a replacement (overflow / drain-replacement).
    if (rec.areaKey && this.byKey.get(rec.areaKey) === areaId) {
      this.byKey.delete(rec.areaKey);
    }
    return rec;
  }

  private requireRecord(areaId: string): DynamicAreaRecord {
    const rec = this.records.get(areaId);
    if (!rec) {
      throw new Error(`Unknown area instance: ${areaId}`);
    }
    return rec;
  }

  private transition(areaId: string, to: DYNAMIC_AREA_STATE): DynamicAreaRecord {
    const rec = this.requireRecord(areaId);
    if (!LEGAL_TRANSITIONS[rec.state].includes(to)) {
      throw new Error(
        `Illegal area-instance transition ${rec.state} → ${to} for ${areaId}`,
      );
    }
    rec.state = to;
    return rec;
  }
}

const LEGAL_TRANSITIONS: Record<DYNAMIC_AREA_STATE, DYNAMIC_AREA_STATE[]> = {
  [DYNAMIC_AREA_STATE.PENDING]: [DYNAMIC_AREA_STATE.READY, DYNAMIC_AREA_STATE.CLOSED],
  [DYNAMIC_AREA_STATE.READY]: [DYNAMIC_AREA_STATE.DRAINING, DYNAMIC_AREA_STATE.CLOSED],
  [DYNAMIC_AREA_STATE.DRAINING]: [DYNAMIC_AREA_STATE.CLOSED],
  [DYNAMIC_AREA_STATE.CLOSED]: [],
};
